// Pure helpers for the proposal-history timeline (SPEC-CHANGE-CANDIDATES
// item 14). Used by ProposalTimeline.svelte to enrich the server-side
// timeline with manifest-side audit stamps held in the SPA's
// catalogue-store cache, and to label each event for display.
//
// The rune-free split keeps these covered by node:test without the
// Svelte compiler, same pattern as guide-content-helpers.ts.

import type {
  ManifestVersionEntry,
  TimelineEvent,
  TimelineEventType,
  VersionManifest
} from './types';

// Find the manifest entry for a given git-hash. The server-side
// timeline endpoint does not carry these audit stamps (no KV
// round-trip per timeline view), so the SPA enriches client-side
// from its catalogue-store cache. The manifest's per-version entry
// carries published_now_at/_by/_reason and dequeued_at/_by/_reason
// when those queue actions have run on the entry. The merged PR's
// candidate_hash (returned by api.loadProposal) is the matching key.
export function findManifestEntryByHash(
  manifest: VersionManifest | null,
  git_hash: string | null | undefined
): ManifestVersionEntry | null {
  if (!manifest || !git_hash) return null;
  for (const jurisdiction of Object.values(manifest.jurisdictions ?? {})) {
    for (const product of Object.values(jurisdiction ?? {})) {
      for (const entry of product?.versions ?? []) {
        if (entry.git_hash === git_hash) return entry;
      }
    }
  }
  return null;
}

// Append the manifest audit stamps (Publish-now and De-queue) to a
// server-supplied timeline and re-sort. The server returns only the
// PR + reviews + commits events; the SPA layer enriches.
export function enrichTimelineWithManifestEntry(
  events: TimelineEvent[],
  entry: ManifestVersionEntry | null
): TimelineEvent[] {
  if (!entry) return events.slice();
  const enriched = events.slice();
  if (entry.published_now_at) {
    enriched.push({
      type: 'published_now',
      actor: { login: entry.published_now_by ?? 'unknown' },
      timestamp_utc: entry.published_now_at,
      summary: 'Published immediately',
      body: entry.published_now_reason || undefined,
      source: 'manifest',
      source_id: entry.catalogue_version
    });
  }
  if (entry.dequeued_at) {
    enriched.push({
      type: 'dequeued',
      actor: { login: entry.dequeued_by ?? 'unknown' },
      timestamp_utc: entry.dequeued_at,
      summary: 'Dequeued from publication',
      body: entry.dequeued_reason || undefined,
      source: 'manifest',
      source_id: entry.catalogue_version
    });
  }
  enriched.sort((a, b) => String(a.timestamp_utc).localeCompare(String(b.timestamp_utc)));
  return enriched;
}

// Per-event-type display attributes the renderer reads. Icons are
// short text glyphs rather than image assets so the timeline does
// not pull in icon-font dependencies; the css-class drives colour.
export interface TimelineEventDisplay {
  icon: string;
  cssClass: string;
  label: string;
}

const DISPLAY_MAP: Record<TimelineEventType, TimelineEventDisplay> = {
  proposal_opened: { icon: '✎', cssClass: 'timeline-event--opened', label: 'Opened' },
  comment: { icon: '💬', cssClass: 'timeline-event--comment', label: 'Comment' },
  request_changes: { icon: '⟲', cssClass: 'timeline-event--request-changes', label: 'Changes requested' },
  revision_submitted: { icon: '↻', cssClass: 'timeline-event--revision', label: 'Revision' },
  approve: { icon: '✓', cssClass: 'timeline-event--approve', label: 'Approved' },
  merged: { icon: '⇲', cssClass: 'timeline-event--merged', label: 'Merged' },
  closed_without_merge: { icon: '✕', cssClass: 'timeline-event--closed', label: 'Closed' },
  dismissed: { icon: '↶', cssClass: 'timeline-event--dismissed', label: 'Dismissed' },
  published_now: { icon: '⚡', cssClass: 'timeline-event--published', label: 'Published now' },
  dequeued: { icon: '⊘', cssClass: 'timeline-event--dequeued', label: 'Dequeued' }
};

export function displayFor(type: TimelineEventType): TimelineEventDisplay {
  return DISPLAY_MAP[type] ?? { icon: '·', cssClass: 'timeline-event--other', label: type };
}

// Format an ISO timestamp into a human-readable local string. The
// SPA uses the browser's locale via toLocaleString; the helper is
// pulled out so node:test can exercise the wrapper directly with a
// known fixed locale and timezone.
export function formatTimelineTimestamp(
  iso: string,
  options?: { locale?: string; timeZone?: string }
): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(options?.locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: options?.timeZone
  });
}
